import { useInteractionStyle } from "./style";
import {
	Accordion,
	AccordionToggleEventHandler,
	mergeClasses,
	Text,
	Button,
} from "@fluentui/react-components";
import { useShadowStyle } from "@/common/theme/shadowStyles";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { shadowByPosition } from "@/common/theme/styleUtils";
import {
	InteractionPanelItemDescription,
	InteractionPanelProps,
	InteractionState,
} from "./types";
import {
	InteractionDirection,
	InteractionDto,
	InteractionHandlingStatus,
	InteractionOperations,
	InteractionStatus,
	QueueFlags,
} from "@/repository/models";
import { useIntl } from "react-intl";
import {
	InteractionMenu,
	allInteractions,
} from "@/components/molecules/interactionMenu";
import { ListItemProps } from "@/components/atoms/list";
import { TagStack } from "@/components/atoms/tagStack";
import { HandIcon } from "@/common/icons";
import { useActiveStyle } from "./activeStyle";
import Timer from "@/components/atoms/timer/Timer";
import { InteractionType } from "@/common/types";
import {
	useCurrentInteractionStore,
	CurrentInteractionState,
	CurrentInteractionActions,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import {
	NavigationActions,
	TabType,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { unParkCall } from "@/repository/queryFns";
import TooltipWithMount from "@/components/atoms/tooltipWithMount/TooltipWithMount";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import {
	getInteractionName,
	interactionCanBePickedUp,
	interactionCanBeResumed,
	mapInteractionStatusToPanelStatus,
} from "@/common/helpers";
import { VoicemailInteractions } from "./voicemails";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { addBreadcrumb } from "@/common/helpers/sentryHelper";
import {
	getInteractionSecondaryInfo,
	getInteractionStatusText,
	getInteractionTypeLabel,
} from "@/common/helpers/supervisorHelper";
import { InteractionIconOverlay } from "@/components/molecules/interactionIconOverlay";
import { useAcs } from "@/common/acs/AcsContext";
import { useInteractionPickUp } from "./useInteractionPickUp";
import { TabHeader } from "@/components/atoms/tabHeader/TabHeader";
import { useAgentInteractionState } from "@/hooks/agent";
import {
	InteractionListAccordionItem,
	InteractionListAccordionItemProps,
} from "@/components/molecules/listAccordionItem";
import { InteractionListIcon } from "@/components/molecules/interactionListIcon";
import { useInteractions } from "./useInteractions";
import { useResponsiveBreakpoints } from "@/hooks/responsive";
import { Flex, GapSize } from "@/components/atoms/flex";
import { Play20Regular, PreviewLink20Regular } from "@fluentui/react-icons";
import { SubHeaderWithDirection } from "@/components/atoms/directionArrow";
import { useEmailInteractionPreview } from "./useEmailInteractionPreview";
import {
	useQueuesStore,
	QueuesState,
} from "@/common/stores/nonpersistingStores/queues";
import { unParkTextInteraction } from "@/common/helpers/parkHelper";

export const InteractionPanel = (props: InteractionPanelProps) => {
	const { shadowTo } = props;
	const classes = useInteractionStyle();
	const activeClasses = useActiveStyle();
	const shadowStyle = useShadowStyle();
	const shadow = useMemo<string | undefined>(
		() => shadowByPosition(shadowTo, shadowStyle),
		[shadowTo, shadowStyle]
	);

	// In the My interactions list the call-direction arrow is shown inline before the
	// contact info instead of as a badge on the interaction icon (see hideDirectionArrow).
	const renderSubHeaderWithDirection = useCallback(
		(
			contactInfo: string | undefined,
			direction: InteractionDirection | undefined
		) =>
			contactInfo ? (
				<SubHeaderWithDirection text={contactInfo} direction={direction} />
			) : (
				""
			),
		[]
	);

	/**
	 * Interaction tags, stacked under the queue name in the card header. Nothing is rendered — and no
	 * space reserved — when the interaction has none. The "headerTags" class ties visibility to the
	 * same container query that shows the queue name, so tags only appear once the panel is wide
	 * enough (CC4ALL-8822).
	 */
	const renderHeaderTags = useCallback(
		(interaction: InteractionDto) =>
			interaction.assignedTags?.length ? (
				<div className={"headerTags"}>
					<TagStack tags={interaction.assignedTags} emptyState="none" />
				</div>
			) : undefined,
		[]
	);

	/**
	 * The same tags as a cluster of colours beside the contact name, for the panel widths where the
	 * queue column is hidden and the stacked version above with it. There is no room for names here,
	 * so they move into the tooltips (CC4ALL-8822).
	 *
	 * Visibility lives on the wrapper rather than on the TagStack itself, exactly as it does for
	 * renderHeaderTags above, so the atom stays unaware of which panel widths it is meant for.
	 */
	const renderInlineHeaderTags = useCallback(
		(interaction: InteractionDto) =>
			interaction.assignedTags?.length ? (
				<div className={classes.inlineHeaderTags}>
					<TagStack
						tags={interaction.assignedTags}
						emptyState="none"
						layout="dots"
					/>
				</div>
			) : undefined,
		[classes.inlineHeaderTags]
	);

	const intl = useIntl();

	const [selectedId, setSelectedId] = useState<string>();

	const { interactions, updateCurrentInteraction } = useInteractions(
		selectedId,
		setSelectedId
	);

	const { isMobile } = useResponsiveBreakpoints();

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const currentInteractionInPreview = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.preview
	);

	const currentInteractionActions = useCurrentInteractionStore(
		(actions: CurrentInteractionActions) => actions
	);

	const setIsMobileDetailViewMode = useNavigationStore(
		(actions: NavigationActions) => actions.setIsMobileDetailViewMode
	);

	const pickUpInteraction = useInteractionPickUp();
	const { showEmailPreview, hideEmailPreview } = useEmailInteractionPreview();

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const [openItems, setOpenItems] = useState<any>(
		appStoreCache[CacheKey.OpenedInteractionSections] ?? [
			InteractionState.Active,
			InteractionState.Waiting,
			InteractionState.Voicemail,
		]
	);

	const [filteredTypes, setFilteredTypes] = useState<InteractionType[]>(
		appStoreCache[CacheKey.InteractionTypeFilterValues] ?? []
	);

	const [filteredQueueIds, setFilteredQueueIds] = useState<string[]>(
		appStoreCache[CacheKey.InteractionQueueIds] ?? []
	);

	const [searchValue, setSearchValue] = useState<string>(
		appStoreCache[CacheKey.InteractionSearchValue] ?? ""
	);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const activeElementRef = useRef({} as HTMLElement);
	const waitingElementRef = useRef({} as HTMLElement);
	const voicemailElementRef = useRef({} as HTMLElement);

	useEffect(() => {
		appStoreActions.setCache(CacheKey.OpenedInteractionSections, openItems);
	}, [openItems]);

	useEffect(() => {
		appStoreActions.setCache(
			CacheKey.InteractionTypeFilterValues,
			filteredTypes
		);
	}, [filteredTypes]);

	useEffect(() => {
		appStoreActions.setCache(CacheKey.InteractionQueueIds, filteredQueueIds);
	}, [filteredQueueIds]);

	useEffect(() => {
		appStoreActions.setCache(CacheKey.InteractionSearchValue, searchValue);
	}, [searchValue]);

	const queuesFlags = useMemo(() => {
		return new Map<string, QueueFlags>(
			queues.map(
				({ id, isPreviewModeEnabled, isPickupEnabled, emailSendType }) => [
					id,
					{ isPreviewModeEnabled, isPickupEnabled, emailSendType },
				]
			)
		);
	}, [queues]);

	const queuesWithSkillsBasedRoutingEnabled = useMemo(() => {
		const set = new Set(
			queues.filter((x) => x.isSkillBasedRoutingEnabled).map((x) => x.id)
		);

		return set;
	}, [queues]);

	const getStartTime = useCallback(
		(activeInteraction: InteractionDto) => {
			const userId = Lockr.get(StorageKeys.UserId);

			if (
				activeInteraction.id === callSession?.id &&
				callSession?.supervisorId === userId
			) {
				return callSession?.supervisingStartDate;
			}

			return activeInteraction.startDate;
		},
		[callSession]
	);

	const {
		agentHasCallInProgressOrTextInOffering,
		agentHasInteractionInOffering,
	} = useAgentInteractionState();

	const getHoverIcon = useCallback(
		(interaction: InteractionDto) => {
			if (interaction.status === InteractionStatus.Offering) {
				return undefined;
			}

			const interactionQueueFlags = queuesFlags.get(interaction.queueId);

			const canBePickedUp = interactionCanBePickedUp(
				{
					...interaction,
					isPickupEnabled: interactionQueueFlags?.isPickupEnabled ?? false,
				},
				agentHasInteractionInOffering,
				agentHasCallInProgressOrTextInOffering
			);

			const canBeResumed = interactionCanBeResumed(
				{ ...interaction },
				agentHasInteractionInOffering,
				agentHasCallInProgressOrTextInOffering
			);

			const canShowPreview =
				interaction.type === InteractionType.Email &&
				interactionQueueFlags?.isPreviewModeEnabled &&
				(canBePickedUp || canBeResumed) &&
				currentInteractionInPreview?.id !== interaction.id;

			const buttons = [];

			if (canShowPreview) {
				buttons.push(
					<TooltipWithMount
						key={"preview"}
						withArrow
						positioning={"below"}
						content={intl.formatMessage({ id: "Interaction.Buttons.Preview" })}
						relationship="label"
					>
						<Button
							appearance="primary"
							className={mergeClasses(
								classes.customPaddingButton,
								classes.listActionButtonSlim
							)}
							icon={<PreviewLink20Regular />}
							onClick={() => {
								showEmailPreview(interaction);
								if (isMobile) {
									setIsMobileDetailViewMode(true);
								}
							}}
						/>
					</TooltipWithMount>
				);
			}

			if (canBeResumed) {
				buttons.push(
					<TooltipWithMount
						key={"resume"}
						withArrow
						positioning={"below"}
						content={intl.formatMessage({ id: "Interaction.Buttons.Resume" })}
						relationship="label"
					>
						<Button
							appearance="primary"
							className={mergeClasses(
								classes.customPaddingButton,
								classes.listActionButtonSlim
							)}
							icon={<Play20Regular />}
							onClick={() => {
								addBreadcrumb(
									"ui_interaction",
									"Unparking interaction",
									interaction
								);
								interaction.type === InteractionType.Call
									? unParkCall(interaction.id)
									: unParkTextInteraction(interaction.id, interaction.type);
							}}
						/>
					</TooltipWithMount>
				);
			} else if (canBePickedUp) {
				buttons.push(
					<TooltipWithMount
						key={"pickup"}
						withArrow
						positioning={"below"}
						content={intl.formatMessage({ id: "Interaction.Buttons.PickUp" })}
						relationship="label"
					>
						<Button
							appearance="primary"
							className={mergeClasses(
								classes.customPaddingButton,
								canShowPreview && classes.listActionButtonSlim,
								canShowPreview && classes.pickUpButtonSlim
							)}
							icon={<HandIcon />}
							onClick={(e) => pickUpInteraction(e, interaction)}
						>
							{!canShowPreview &&
								intl.formatMessage({ id: "InteractionMenu.PickUp" })}
						</Button>
					</TooltipWithMount>
				);
			}

			if (buttons.length === 0) {
				return undefined;
			}

			if (buttons.length === 1) {
				return buttons[0];
			}

			return <Flex gap={GapSize.ExtraSmall}>{buttons}</Flex>;
		},
		[
			queuesFlags,
			agentHasInteractionInOffering,
			agentHasCallInProgressOrTextInOffering,
			currentInteractionInPreview,
			intl,
			classes,
			showEmailPreview,
			isMobile,
			setIsMobileDetailViewMode,
			pickUpInteraction,
		]
	);

	const getWaitingEndMedia = useCallback(
		(item: ListItemProps) => getHoverIcon(item.data as InteractionDto),
		[getHoverIcon]
	);

	const getFilteredInteractions = (interactions: InteractionDto[]) => {
		let result: InteractionDto[];
		result = getFilteredByTypeInteractions(interactions);
		result = getFilteredBySearchValueInteractions(result);
		result = getFilteredByQueueInteractions(result);

		return result;
	};

	const isSearchValueFoundInInteraction = (interaction: InteractionDto) => {
		return (
			interaction.name?.toLowerCase().startsWith(searchValue.toLowerCase()) ||
			interaction.contactInformation
				?.toLowerCase()
				.startsWith(searchValue.toLowerCase()) ||
			interaction.description
				?.toLowerCase()
				.startsWith(searchValue.toLowerCase())
		);
	};

	const isSearchValueFoundInWrapUp = (interaction: InteractionDto) => {
		const wrapUpInfo = appStoreCache[CacheKey.WrapUpInteractionInfo];

		return (
			wrapUpInfo?.id === interaction.id &&
			(wrapUpInfo?.name?.toLowerCase().startsWith(searchValue.toLowerCase()) ||
				wrapUpInfo?.contactInformation
					?.toLowerCase()
					.startsWith(searchValue.toLowerCase()) ||
				wrapUpInfo?.description
					?.toLowerCase()
					.startsWith(searchValue.toLowerCase()))
		);
	};

	const getFilteredBySearchValueInteractions = (
		interactionsToFilter: InteractionDto[]
	) => {
		return searchValue.length > 0
			? interactionsToFilter.filter(
					(i: InteractionDto) =>
						isSearchValueFoundInInteraction(i) || isSearchValueFoundInWrapUp(i)
				)
			: interactionsToFilter;
	};

	const getFilteredByTypeInteractions = (
		interactionsToFilter: InteractionDto[]
	) => {
		return filteredTypes.length > 0
			? interactionsToFilter.filter((i: InteractionDto) =>
					filteredTypes.includes(i.type)
				)
			: interactionsToFilter;
	};

	const getFilteredByQueueInteractions = (
		interactionsToFilter: InteractionDto[]
	) => {
		return filteredQueueIds.length > 0
			? interactionsToFilter.filter((i: InteractionDto) =>
					filteredQueueIds.includes(i.queueId)
				)
			: interactionsToFilter;
	};

	const { ongoingCalls } = useAcs();
	const [ongoingUnifyInteractions, setOngoingUnifyInteractions] = useState<
		InteractionDto[]
	>([]);

	const accordionActivePanelView = useMemo<ListItemProps[]>(() => {
		const wrapUpInfo = appStoreCache[CacheKey.WrapUpInteractionInfo];

		const mapInteraction = (i: InteractionDto) => {
			let subHeader = "";
			if (!!i.contactInformation && i.contactInformation !== i.name) {
				subHeader = i.contactInformation;
			} else if (
				wrapUpInfo?.id === i.id &&
				wrapUpInfo?.contactInformation &&
				wrapUpInfo?.contactInformation != wrapUpInfo?.name
			) {
				subHeader = wrapUpInfo?.contactInformation;
			}

			if (!subHeader) {
				subHeader = getInteractionTypeLabel(i.type, intl) ?? "";
			}

			const header = !!i.name
				? i.name
				: wrapUpInfo?.id === i.id && wrapUpInfo?.name;

			return {
				key: i.id,
				header: getInteractionName(i.type, header, intl),
				headerAdornment: renderInlineHeaderTags(i),
				subHeader: renderSubHeaderWithDirection(subHeader, i.direction),
				content:
					i.type !== InteractionType.Voicemail
						? !!i.description
							? i.description
							: wrapUpInfo?.id === i.id && wrapUpInfo?.description
						: "",
				media: isMobile ? (
					<InteractionListIcon
						interaction={i}
						interactionHandlingStatus={InteractionHandlingStatus.Active}
						hideDirectionArrow
					/>
				) : (
					<InteractionIconOverlay
						interaction={i}
						itemPanelStatus={InteractionPanelItemDescription.Active}
						interactionHandlingStatus={InteractionHandlingStatus.Active}
						isHighLighted={currentInteraction?.id == i.id}
						mountNode={activeElementRef}
						isSkillBasedRoutingEnabled={queuesWithSkillsBasedRoutingEnabled.has(
							i.queueId
						)}
						hideDirectionArrow
					/>
				),
				headerMedia: (
					<>
						<Flex column className={classes.listHeaderQueueColumn}>
							<div className={"additionalContent"} title={i.queueName}>
								{i.queueName}
							</div>
							{renderHeaderTags(i)}
						</Flex>
						{!i.isOnHold ? (
							<Timer startDate={getStartTime(i)} />
						) : (
							<Text className={classes.statusText}>
								{intl.formatMessage({
									id: "Interaction.Operations.Parked",
								})}
							</Text>
						)}
					</>
				),
				onClick: (_: any) => {
					hideEmailPreview();
					setIsMobileDetailViewMode(true);
					updateCurrentInteraction(_, i, true);
				},
			};
		};

		const activeIds = new Set(
			interactions?.active?.map((i: InteractionDto) => i.id) ?? []
		);

		const ongoingInteractionDtos: InteractionDto[] = [];

		const extraUnifyCalls = Array.from(ongoingCalls?.values() ?? [])
			.filter((ongoing) => !activeIds.has(ongoing.call.id))
			.map((ongoing) => {
				const interaction: InteractionDto = {
					changeType: InteractionOperations.Added,
					contactInformation: ongoing.info,
					description: "",
					direction: ongoing.direction,
					emailTransferInitiatorId: "",
					externalUrl: "",
					id: ongoing.call.id,
					isUnify: true,
					name: ongoing.name,
					queueId: "",
					queueName: "",
					queuePriority: 1,
					sessionId: ongoing.call.id,
					startDate: ongoing.date.toISOString(),
					status: InteractionStatus.Assigned,
					type: InteractionType.Call,
					wasTransferred: false,
					handlingStatus: InteractionHandlingStatus.Active,
					isLastMessageSeen: false,
					isIVRWarmTransfer: false,
				};

				ongoingInteractionDtos.push(interaction);

				return mapInteraction(interaction);
			});

		if (
			ongoingInteractionDtos.length > 0 &&
			ongoingUnifyInteractions.length !== ongoingInteractionDtos.length
		) {
			const activeInteraction = ongoingInteractionDtos.find(
				(item2) =>
					!ongoingUnifyInteractions.some((item1) => item1.id === item2.id)
			);

			if (activeInteraction) {
				appStoreActions.setCache(
					CacheKey.CurrentInteractionId,
					activeInteraction.id
				);

				currentInteractionActions.showActive(
					{
						id: activeInteraction.id,
						type: activeInteraction.type,
						contactInformation: activeInteraction.contactInformation,
						description: activeInteraction.description,
						name: activeInteraction.name,
						queueName: activeInteraction.queueName,
						startDate: getStartTime(activeInteraction),
						firstAgentAssignedAt: activeInteraction.firstAgentAssignedAt,
						queueId: activeInteraction.queueId,
						firstAgentId: activeInteraction.firstAgentId,
						voicemailFilePath: activeInteraction.voicemailFilePath,
						voicemailIsTranscriptionProcessed:
							activeInteraction.voicemailIsTranscriptionProcessed,
						voicemailExpireDate: activeInteraction.voicemailExpireDate,
						entryPointEmail: activeInteraction.entryPointEmail,
						direction: activeInteraction.direction,
						monitoringSupervisorId: activeInteraction.monitoringSupervisorId,
						isOnHold: activeInteraction.isOnHold,
						sendType: queuesFlags.get(activeInteraction.queueId)?.emailSendType,
						activeVariableText: activeInteraction.activeVariableText,
						activeVariableTextIsAiGenerated:
							activeInteraction.activeVariableTextIsAiGenerated,
						offeringVariableText: activeInteraction.offeringVariableText,
						offeringVariableTextIsAiGenerated:
							activeInteraction.offeringVariableTextIsAiGenerated,
						wasTransferred: activeInteraction.wasTransferred,
						sessionId: activeInteraction.sessionId,
						isUnify: activeInteraction.isUnify,
						agentCallingEnabled: activeInteraction.agentCallingEnabled,
					},
					true
				);

				setSelectedId(activeInteraction.id);
			}
		}

		const activeItemRemoved = ongoingUnifyInteractions.some(
			(item1) =>
				item1.id === currentInteraction?.id &&
				!ongoingInteractionDtos.some((item2) => item2.id === item1.id)
		);

		if (activeItemRemoved) {
			setSelectedId("");
			currentInteractionActions.hideActive();
			appStoreActions.removeCaches([CacheKey.CurrentInteractionId]);
		}

		setOngoingUnifyInteractions(ongoingInteractionDtos);

		const result = [
			...(interactions?.active
				? getFilteredInteractions(interactions.active).map(mapInteraction)
				: []),
			...extraUnifyCalls,
		];

		return result;
	}, [
		interactions,
		filteredTypes,
		filteredQueueIds,
		callSession,
		currentInteraction,
		searchValue,
		activeElementRef.current,
		ongoingCalls,
		isMobile,
		queuesFlags,
		queues,
		queuesWithSkillsBasedRoutingEnabled,
		hideEmailPreview,
		renderSubHeaderWithDirection,
		renderHeaderTags,
		renderInlineHeaderTags,
	]);

	const accordionWaitingPanelView = useMemo<ListItemProps[]>(
		() =>
			interactions?.waiting
				? getFilteredInteractions(interactions.waiting).map(
						(i: InteractionDto) => {
							const header = i.name || i.contactInformation;

							return {
								key: i.id,
								header: getInteractionName(i.type, header, intl),
								headerAdornment: renderInlineHeaderTags(i),
								subHeader: renderSubHeaderWithDirection(
									getInteractionSecondaryInfo(i, intl),
									i.direction
								),
								content: i.description,
								media: isMobile ? (
									<InteractionListIcon
										interaction={i}
										interactionHandlingStatus={
											InteractionHandlingStatus.Waiting
										}
										hideDirectionArrow
									/>
								) : (
									<InteractionIconOverlay
										interaction={i}
										itemPanelStatus={mapInteractionStatusToPanelStatus(
											i.status
										)}
										interactionHandlingStatus={
											InteractionHandlingStatus.Waiting
										}
										mountNode={waitingElementRef}
										isSkillBasedRoutingEnabled={queuesWithSkillsBasedRoutingEnabled.has(
											i.queueId
										)}
										hideDirectionArrow
									/>
								),
								headerMedia:
									i.status == InteractionStatus.Offering ||
									i.status == InteractionStatus.CallbackOffering ? (
										<>
											<Flex column className={classes.listHeaderQueueColumn}>
												<div className={"additionalContent"} title={i.queueName}>
													{i.queueName}
												</div>
												{renderHeaderTags(i)}
											</Flex>
											<Text className={classes.statusText}>
												{intl.formatMessage({
													id: "InteractionPanel.Offering",
												})}
											</Text>
										</>
									) : (
										<>
											<Flex column className={classes.listHeaderQueueColumn}>
												<div className={"additionalContent"} title={i.queueName}>
													{i.queueName}
												</div>
												{renderHeaderTags(i)}
											</Flex>
											<Flex column vAlign="flex-end">
												<Timer startDate={i.startDate} />
												<Text className={classes.statusText}>
													{getInteractionStatusText(i.status, intl)}
												</Text>
											</Flex>
										</>
									),
								data: i,
							};
						}
					)
				: [],
		[
			interactions,
			filteredTypes,
			filteredQueueIds,
			searchValue,
			waitingElementRef.current,
			queuesWithSkillsBasedRoutingEnabled,
			renderSubHeaderWithDirection,
			renderHeaderTags,
			renderInlineHeaderTags,
		]
	);

	const toggleInteractionCategoryVisibility: AccordionToggleEventHandler<
		InteractionState
	> = (_, data) => {
		setOpenItems(data.openItems);
	};

	const voicemailRefCallback = (el: HTMLDivElement | null) => {
		if (el !== null) {
			voicemailElementRef.current = el;
		}
	};

	const accordionConfigsActiveAndWaiting: InteractionListAccordionItemProps[] =
		useMemo(
			() => [
				{
					value: InteractionState.Active,
					items: accordionActivePanelView,
					listClassNames: {
						item: activeClasses.item,
						itemSelected: activeClasses.itemSelected,
						mainTextSelected: activeClasses.mainTextSelected,
						endMedia: classes.interactionTimer,
						shadow: shadow,
						headerMedia: classes.listHeaderMedia,
						startMedia: classes.iconCircle,
						startMediaSelected: classes.iconCircleSelected,
					},
					ref: (el) => {
						if (el !== null) {
							activeElementRef.current = el;
						}
					},
					unfilteredNumberOfItems: interactions?.active?.length ?? 0,
					selectedId: selectedId,
				},
				{
					value: InteractionState.Waiting,
					items: accordionWaitingPanelView,
					listClassNames: {
						endMedia: classes.interactionTimer,
						containerHeight: classes.bottomContainerHeight,
						shadow: shadow,
						headerMedia: classes.listHeaderMedia,
						itemSelected: activeClasses.itemInPreview,
						mainTextSelected: activeClasses.mainTextSelected,
						startMedia: classes.iconCircle,
						startMediaSelected: classes.iconCircleSelected,
					},
					ref: (el) => {
						if (el !== null) {
							waitingElementRef.current = el;
						}
					},
					unfilteredNumberOfItems: interactions?.waiting?.length ?? 0,
					selectedId: currentInteractionInPreview?.id,
					getEndMedia: getWaitingEndMedia,
				},
			],
			[
				accordionActivePanelView,
				accordionWaitingPanelView,
				activeClasses,
				classes,
				selectedId,
				currentInteractionInPreview,
				interactions,
				queuesWithSkillsBasedRoutingEnabled,
				getWaitingEndMedia,
			]
		);

	return (
		<div
			className={classes.panelContainer}
			style={{ width: isMobile ? "100%" : "unset" }}
		>
			{isMobile && (
				<TabHeader tab={TabType.Interactions} noBottomPadding={true} />
			)}
			<InteractionMenu
				className={classes.interactionsMenu}
				incomingTypes={allInteractions}
				searchText={searchValue}
				onTypeFilterChange={setFilteredTypes}
				onQueueFilterChange={setFilteredQueueIds}
				onSearchChange={setSearchValue}
			/>
			<div className={mergeClasses(classes.container, shadow)}>
				<Accordion
					openItems={openItems}
					onToggle={toggleInteractionCategoryVisibility}
					multiple
					collapsible
				>
					{accordionConfigsActiveAndWaiting.map((x) => (
						<InteractionListAccordionItem {...x} />
					))}
					<VoicemailInteractions
						hideVoicemails={
							filteredTypes.length > 0
								? !filteredTypes.includes(InteractionType.Voicemail)
								: false
						}
						classesName={{
							interactionTimer: classes.interactionTimer,
							listHeaderMedia: classes.listHeaderMedia,
							listHeaderQueueColumn: classes.listHeaderQueueColumn,
							startMedia: classes.iconCircle,
						}}
						getHoverIcon={getHoverIcon}
						renderHeaderTags={renderHeaderTags}
						renderInlineHeaderTags={renderInlineHeaderTags}
						refCallback={voicemailRefCallback}
						searchText={searchValue}
						queueIds={filteredQueueIds}
					/>
				</Accordion>
			</div>
		</div>
	);
};
